import React, { useState, useEffect, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';
import { Box, Paper, Typography, Button, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';

const BarChart = ({ plotData, defaultTitle, xlabel, ylabel, titleKey }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => localStorage.getItem(`mocc_title_${titleKey}`) || defaultTitle || '');
  const [xLabel, setXLabel] = useState(() => localStorage.getItem(`mocc_xlabel_${titleKey}`) || xlabel || '');
  const [yLabel, setYLabel] = useState(() => localStorage.getItem(`mocc_ylabel_${titleKey}`) || ylabel || '');
  const [showEditor, setShowEditor] = useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const x = plotData.map(d => d.grade);
    const yMean = plotData.map(d => d.lra_mean);
    const yQ = plotData.map(d => d.lra_q);
    const ciNormLow = plotData.map(d => d.ci_norm_low || d.lra_mean);
    const ciNormHigh = plotData.map(d => d.ci_norm_high || d.lra_mean);
    const ciTLow = plotData.map(d => d.ci_t_low || d.ci_norm_low || d.lra_mean);
    const ciTHigh = plotData.map(d => d.ci_t_high || d.ci_norm_high || d.lra_mean);
    
    const traces = [
      { x, y: yMean, type: 'bar', name: 'LRA Mean', marker: { color: '#3498db' } },
      { x, y: yQ, type: 'bar', name: 'LRA Quantile', marker: { color: '#e67e22' } },
      { x, y: ciNormHigh, type: 'bar', name: 'Normal CI', marker: { color: '#2ecc71' } },
      { x, y: ciTHigh, type: 'bar', name: 'Student CI', marker: { color: '#e74c3c' } }
    ];
    
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t, xaxis: { title: xl, tickangle: -45, automargin: true }, yaxis: { title: yl },
      template: 'plotly_white', height: 500, barmode: 'group',
      margin: { t: 60, b: 120, l: 60, r: 20 },
      legend: { orientation: 'h', y: -0.3, x: 0.5, xanchor: 'center' }
    }, { responsive: true });
  };

  useEffect(() => { if (plotData && plotData.length > 0) renderPlot(title, xLabel, yLabel); }, [plotData, title, xLabel, yLabel]);

  const applyTitles = () => {
    renderPlot(title, xLabel, yLabel);
    localStorage.setItem(`mocc_title_${titleKey}`, title);
    localStorage.setItem(`mocc_xlabel_${titleKey}`, xLabel);
    localStorage.setItem(`mocc_ylabel_${titleKey}`, yLabel);
    setShowEditor(false);
  };

  const [showTable, setShowTable] = useState(false);

  if (!plotData || plotData.length === 0) return <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>No data available. Run analysis first.</div>;

  const tableHeaders = Object.keys(plotData[0] || {});

  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{title}</h4>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => setShowTable(!showTable)} style={{ padding: '2px 8px', fontSize: '11px', background: '#34495e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{showTable ? 'Hide Table' : 'Show Table'}</button>
          <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️ Edit</button>
        </div>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, fontSize: '12px', padding: '4px' }} />
          <input placeholder="X axis" value={xLabel} onChange={e => setXLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <input placeholder="Y axis" value={yLabel} onChange={e => setYLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <button onClick={applyTitles} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '500px' }}></div>
      {showTable && (
        <div style={{ marginTop: '12px', maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
              <tr>{tableHeaders.map(h => <th key={h} style={{ padding: '6px' }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {plotData.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  {tableHeaders.map(h => <td key={h} style={{ padding: '4px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MoCC = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(null);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [saveColName, setSaveColName] = useState('MoC_C_LGD');
  const [bootstrapIterations, setBootstrapIterations] = useState(500);
  const [confidenceLevel, setConfidenceLevel] = useState(95);

  const [groups, setGroups] = useState({ segmentation: false, target: false, bootstrap: false });
  const [formData, setFormData] = useState({ Default_Date: null, Calibration_segment: null, Grade: null, Target_variable: null });

  // ========== ADDED: Analysis state variables ==========
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [allDatasets, setAllDatasets] = useState([]);

  // ========== ADDED: Mode detection ==========
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  // ========== ADDED: Load saved analysis on mount ==========
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    }
  }, []);

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setAllDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        rows: ds.row_count,
        columns: ds.column_count
      }));
      setAllDatasets(datasets);
    } catch (err) {
      console.error('Failed to load datasets', err);
    }
  };
  useEffect(() => { fetchDatasets(); }, []);

  // ========== ADDED: Load saved analysis function ==========
  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      if (analysis.input_parameters) {
        if (analysis.input_parameters.dataset_id) {
          setDatasetInfo({ id: analysis.input_parameters.dataset_id, name: analysis.input_parameters.dataset_name, rows: analysis.input_parameters.rows, columns: analysis.input_parameters.columns });
          setDatasetLoaded(true);
          setColumns(analysis.input_parameters.columns || []);
        }
        setFormData({
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          Calibration_segment: analysis.input_parameters.Calibration_segment ? { value: analysis.input_parameters.Calibration_segment, label: analysis.input_parameters.Calibration_segment } : null,
          Grade: analysis.input_parameters.Grade ? { value: analysis.input_parameters.Grade, label: analysis.input_parameters.Grade } : null,
          Target_variable: analysis.input_parameters.Target_variable ? { value: analysis.input_parameters.Target_variable, label: analysis.input_parameters.Target_variable } : null
        });
        if (analysis.input_parameters.bootstrapIterations) setBootstrapIterations(analysis.input_parameters.bootstrapIterations);
        if (analysis.input_parameters.confidenceLevel) setConfidenceLevel(analysis.input_parameters.confidenceLevel);
        if (analysis.input_parameters.saveColName) setSaveColName(analysis.input_parameters.saveColName);
        if (analysis.input_parameters.results) setResults(analysis.input_parameters.results);
      }
      if (analysis.output_result) { setStep(2); toast.success('Analysis loaded with results'); }
      else if (analysis.input_parameters) { setStep(1); toast.success('Analysis loaded. Continue to run analysis steps.'); }
    } catch (err) { toast.error('Failed to load analysis'); }
    finally { setIsLoadingAnalysis(false); }
  };

  // ========== ADDED: Save Analysis function ==========
  const handleSaveAnalysis = async () => {
    if (!datasetInfo) { toast.error('No dataset selected'); return; }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) { toast.error('No project selected'); return; }
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'moc_c',
        description: analysisDescription,
        input_parameters: {
          dataset_id: datasetInfo.id, dataset_name: datasetInfo.name, rows: datasetInfo.rows, columns: datasetInfo.columns,
          Default_Date: formData.Default_Date?.value || '',
          Calibration_segment: formData.Calibration_segment?.value || '',
          Grade: formData.Grade?.value || '',
          Target_variable: formData.Target_variable?.value || '',
          bootstrapIterations, confidenceLevel, saveColName, results
        },
        output_result: results,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) { console.error('Save error:', err); toast.error('Failed to save analysis'); }
    finally { setLoading(false); }
  };

  // ========== ADDED: Update Metadata function ==========
  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) { toast.error('No analysis saved yet'); return; }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, { status: analysisStatus, description: analysisDescription });
      toast.success('Status and description updated');
    } catch (err) { toast.error('Failed to update'); }
    finally { setUpdatingMetadata(false); }
  };

  // ========== ADDED: Add Comment function ==========
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const commentObj = { id: Date.now(), text: newComment, created_at: new Date().toISOString(), user_id: localStorage.getItem('user_id') || 'user', user_email: localStorage.getItem('user_email') || 'Unknown' };
    const updatedComments = [...comments, commentObj];
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments); setNewComment(''); toast.success('Comment added');
      } catch (err) { toast.error('Failed to add comment'); }
      finally { setSavingComment(false); }
    } else {
      setComments(updatedComments); setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo({ id: data.id, name: `Dataset ${data.id}`, rows: data.rows, columns: data.columns });
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  useEffect(() => {
    if (datasetInfo && allDatasets.length > 0) {
      const datasetFromList = allDatasets.find(d => d.id === datasetInfo.id);
      if (datasetFromList && datasetFromList.name !== datasetInfo.name) {
        setDatasetInfo(prev => ({ ...prev, name: datasetFromList.name }));
      }
    }
  }, [allDatasets, datasetInfo]);

  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));
  const toggleGroup = (group) => setGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const saveParams = async () => {
    if (!formData.Default_Date || !formData.Target_variable) { toast.error('Please select Default Date and Target Variable'); return; }
    if (!formData.Grade && !formData.Calibration_segment) { toast.error('Please select Grade or Calibration segment'); return; }
    setLoading(true);
    try {
      await api.post('/api/moc-c/step0', {
        Default_Date: formData.Default_Date.value,
        Calibration_segment: formData.Calibration_segment?.value || '',
        Grade: formData.Grade?.value || '',
        Target_variable: formData.Target_variable.value,
        n_bootstrap: bootstrapIterations,
        conf_level: confidenceLevel
      });
      toast.success('Parameters saved'); setStep(1);
    } catch (err) { toast.error('Failed to save parameters'); }
    finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/moc-c/step1', {
        Default_Date: formData.Default_Date.value,
        Calibration_segment: formData.Calibration_segment?.value || '',
        Grade: formData.Grade?.value || '',
        Target_variable: formData.Target_variable.value,
        n_bootstrap: bootstrapIterations,
        conf_level: confidenceLevel
      });
      setResults(response.data.results);
      toast.success(response.data.message); setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || 'Analysis failed'); }
    finally { setLoading(false); }
  };

  const saveResults = async () => {
    if (!saveColName.trim()) { toast.error('Enter a column name'); return; }
    setLoadingStep('save');
    try {
      const response = await api.post('/api/moc-c/save-results', { column_name: saveColName.trim() });
      toast.success(response.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setLoadingStep(null); }
  };

  const exportResults = async () => {
    const customTitles = {
      title: localStorage.getItem('mocc_title_mocc') || '',
      xlabel: localStorage.getItem('mocc_xlabel_mocc') || 'Grade',
      ylabel: localStorage.getItem('mocc_ylabel_mocc') || 'LGD'
    };
    try {
      const response = await api.post('/api/moc-c/export-with-titles', customTitles, { responseType: 'blob' }).catch(() => api.get('/api/moc-c/export', { responseType: 'blob' }));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'mocc_results.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to export'); }
  };

  const sc = (t) => {
    if (t === 0) setStep(0);
    else if (t === 1 && datasetLoaded && formData.Default_Date) setStep(1);
    else if (t === 2 && results) setStep(2);
  };

  const cs = { control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }), valueContainer: (b) => ({ ...b, padding: '0 8px' }), placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }), menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };

  const groupStyle = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'visible', position: 'relative' };
  const groupHeaderStyle = { background: '#f5f7fa', padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '14px', color: '#2c3e50', opacity: isViewMode ? 0.6 : 1 };
  const groupContentStyle = { padding: '16px', background: 'white', overflow: 'visible' };

  return (
    <DiscretizationLayout title="MoC C - Margin of Conservatism" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {/* ========== ADDED: View mode banner ========== */}
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button onClick={() => { const url = new URL(window.location.href); url.searchParams.set('mode', 'edit'); window.location.href = url.toString(); }} style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Modify</button>
        </div>
      )}

      {step === 0 && (
        <>
          <DatasetSelector projectId={new URLSearchParams(window.location.search).get("project_id")} apiPrefix="/api/moc-c" onDatasetLoaded={hdl} />
          
          {/* ========== ADDED: Dataset info display ========== */}
          {datasetInfo && (
            <div style={{ marginTop: '16px', marginBottom: '16px', padding: '12px', background: '#e8f8f5', borderRadius: '8px', borderLeft: '4px solid #1abc9c' }}>
              <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>📌 Currently selected dataset:</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>{datasetInfo.name}</div>
              <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>{datasetInfo.rows?.toLocaleString()} rows • {datasetInfo.columns?.length || 0} columns</div>
            </div>
          )}

          {columns.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={groupStyle}><div style={groupHeaderStyle} onClick={() => toggleGroup('segmentation')}><span>🎯 1. Segmentation</span><span>{groups.segmentation ? '−' : '+'}</span></div>
                {groups.segmentation && <div style={groupContentStyle}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div><label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Calibration segment (optional)</label><Select options={columnOptions} value={formData.Calibration_segment} onChange={v => hfc('Calibration_segment', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select calibration segment..." isDisabled={isViewMode} /></div>
                    <div><label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Grade (optional)</label><Select options={columnOptions} value={formData.Grade} onChange={v => hfc('Grade', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select grade column..." isDisabled={isViewMode} /></div>
                  </div>
                  <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '8px' }}>⚠️ At least one required</p>
                </div>}
              </div>

              <div style={groupStyle}><div style={groupHeaderStyle} onClick={() => toggleGroup('target')}><span>📊 2. Target variable and Date</span><span>{groups.target ? '−' : '+'}</span></div>
                {groups.target && <div style={groupContentStyle}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div><label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Default Date *</label><Select options={columnOptions} value={formData.Default_Date} onChange={v => hfc('Default_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select default date column..." isDisabled={isViewMode} /></div>
                    <div><label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Target Variable *</label><Select options={columnOptions} value={formData.Target_variable} onChange={v => hfc('Target_variable', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select target variable..." isDisabled={isViewMode} /></div>
                  </div>
                </div>}
              </div>

              <div style={groupStyle}><div style={groupHeaderStyle} onClick={() => toggleGroup('bootstrap')}><span>🎲 3. Bootstrap Settings</span><span>{groups.bootstrap ? '−' : '+'}</span></div>
                {groups.bootstrap && <div style={groupContentStyle}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div><label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Bootstrap Iterations</label><input type="number" value={bootstrapIterations} onChange={e => setBootstrapIterations(parseInt(e.target.value))} style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' }} disabled={isViewMode} /></div>
                    <div><label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Confidence Level (%)</label><input type="number" value={confidenceLevel} onChange={e => setConfidenceLevel(parseInt(e.target.value))} style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' }} disabled={isViewMode} /></div>
                  </div>
                </div>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button onClick={saveParams} disabled={loading || isViewMode} style={{ padding: '8px 28px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading || isViewMode ? 'not-allowed' : 'pointer', fontSize: '13px' }}>{loading ? 'Saving...' : 'Continue →'}</button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div>
          <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Review Parameters</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px', fontSize: '13px' }}>
              <p><strong>Default Date:</strong> {formData.Default_Date?.label}</p>
              <p><strong>Target Variable:</strong> {formData.Target_variable?.label}</p>
              <p><strong>Grade:</strong> {formData.Grade?.label || "(not provided)"}</p>
              <p><strong>Calibration:</strong> {formData.Calibration_segment?.label || "(not provided)"}</p>
              <p><strong>Bootstrap:</strong> {bootstrapIterations}</p>
              <p><strong>Confidence:</strong> {confidenceLevel}%</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={runAnalysis} disabled={loading || isViewMode} style={{ padding: '8px 24px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading || isViewMode ? 'not-allowed' : 'pointer' }}>{loading ? 'Running...' : 'Run Analysis →'}</button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <BarChart plotData={results.bar_data} defaultTitle="MoC C LGD by Grade" xlabel="Grade" ylabel="LGD" titleKey="mocc" />

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div><label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Column Name</label><input type="text" value={saveColName} onChange={e => setSaveColName(e.target.value)} style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', width: '150px' }} disabled={isViewMode} /></div>
              <button onClick={saveResults} disabled={loadingStep === 'save' || isViewMode} style={{ padding: '8px 20px', background: loadingStep === 'save' || isViewMode ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: loadingStep === 'save' || isViewMode ? 'not-allowed' : 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}>{loadingStep === 'save' ? 'Saving...' : '💾 Save to Dataset'}</button>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
              {/* <button onClick={exportResults} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button> */}
            </div>
          </div>

          {/* ========== ADDED: Comments Section ========== */}
          <div style={{ marginTop: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ background: '#f5f7fa', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', fontWeight: 600, fontSize: '14px', color: '#2c3e50' }} onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}>
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>{isCommentsExpanded ? '−' : '+'}</span>
            </div>
            {isCommentsExpanded && (
              <div style={{ padding: '16px', background: 'white' }}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>No comments yet. Be the first to add a comment.</Typography>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} style={{ padding: '10px 12px', marginBottom: '8px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '3px solid #1abc9c' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>{comment.user_email || comment.user_id || 'Unknown'}</span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>{new Date(comment.created_at).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>{comment.text}</div>
                      </div>
                    ))
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment or question..." style={{ flex: 1, padding: '10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '8px', resize: 'vertical', minHeight: '60px', fontFamily: 'inherit' }} />
                  <button onClick={handleAddComment} disabled={savingComment || !newComment.trim()} style={{ padding: '8px 20px', background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '8px', cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer', fontSize: '13px', height: 'fit-content' }}>{savingComment ? 'Sending...' : 'Add Comment'}</button>
                </div>
                {!savedAnalysisId && comments.length > 0 && <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>💡 Comments will be permanently saved when you click "Save Analysis"</Typography>}
              </div>
            )}
          </div>

          {/* ========== ADDED: Professional Action Area ========== */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect value={analysisStatus} onChange={(e) => setAnalysisStatus(e.target.value)} label="Status">
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            <TextField fullWidth multiline rows={3} label="Description" value={analysisDescription} onChange={(e) => setAnalysisDescription(e.target.value)} placeholder="Describe the purpose, methodology, or key findings of this MoC C analysis..." sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={() => setStep(0)} disabled={isViewMode} size="small">New Analysis</Button>
              <Button variant="contained" onClick={exportResults} size="small" sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}>📥 Export</Button>
              <Button variant="contained" onClick={handleSaveAnalysis} disabled={!results || isViewMode} size="small" sx={{ bgcolor: results && !isViewMode ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}>💾 Save Analysis</Button>
            </Box>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default MoCC;